// Состояния загрузки/ошибки/пустоты и мелкие презентационные блоки дашбордов.
// Только разметка + тема; без бизнес-логики.

import type { ReactNode } from 'react'

/** Спиннер-строка для состояния загрузки. */
export function Loading({ label = 'Загрузка…' }: { label?: string }): React.JSX.Element {
  return (
    <div className="state">
      <span className="spin" />
      <span>{label}</span>
    </div>
  )
}

/** Блок ошибки в красной рамке. */
export function ErrorState({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="state state--error" role="alert">
      <span>⚠</span>
      <span>{message}</span>
    </div>
  )
}

/** Нейтральное «нет данных». */
export function EmptyState({ message }: { message: string }): React.JSX.Element {
  return <div className="state">{message}</div>
}

/**
 * Три состояния запроса одним компонентом: грузится → ошибка → пусто.
 * Раньше эта тройка условий переписывалась на каждой странице (47 раз) —
 * и кое-где условие «пусто» забывало исключить loading/error.
 *
 * Возвращает null, когда данные есть и их можно рисовать.
 */
export function AsyncState({
  query,
  isEmpty,
  empty,
  loadingLabel
}: {
  query: { loading: boolean; error: string | null }
  /** Данные загрузились, но список пуст. */
  isEmpty?: boolean
  /** Текст для пустого списка; без него пустое состояние не показывается. */
  empty?: string
  loadingLabel?: string
}): React.JSX.Element | null {
  if (query.loading) return <Loading label={loadingLabel} />
  if (query.error) return <ErrorState message={query.error} />
  if (isEmpty && empty) return <EmptyState message={empty} />
  return null
}

/** Заголовок секции с необязательной мета-подписью справа. */
export function SectionHead({
  title,
  meta
}: {
  title: string
  meta?: ReactNode
}): React.JSX.Element {
  return (
    <div className="section-head">
      <div className="section-head__title">{title}</div>
      {meta != null && <div className="section-head__meta">{meta}</div>}
    </div>
  )
}

/** Одна KPI-карточка. */
export function Kpi({
  label,
  value,
  sub,
  gold,
  accent
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  gold?: boolean
  accent?: boolean
}): React.JSX.Element {
  return (
    <div className={`kpi${accent ? ' kpi--accent' : ''}`}>
      <div className="kpi__label">{label}</div>
      <div className={`kpi__value${gold ? ' kpi__value--gold' : ''}`}>{value}</div>
      {sub != null && <div className="kpi__sub">{sub}</div>}
    </div>
  )
}

/** Шапка страницы: надзаголовок, заголовок, лид-абзац. */
export function PageHead({
  eyebrow,
  title,
  lead
}: {
  eyebrow: string
  title: string
  lead: string
}): React.JSX.Element {
  return (
    <>
      <div className="eyebrow">{eyebrow}</div>
      <h2 className="page-title">{title}</h2>
      <p className="page-lead">{lead}</p>
    </>
  )
}
